import React, { useState, useEffect } from 'react';
import { openShiftsAPI, visitsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Broadcast, MapPin, Clock, Star, Users, ArrowClockwise, CheckCircle, ChatDots } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusMeta = {
  open: { label: 'Broadcasting', color: 'bg-blue-100 text-blue-800' },
  requested: { label: 'Requests Received', color: 'bg-amber-100 text-amber-800' },
  assigned: { label: 'Assigned', color: 'bg-green-100 text-green-800' },
};

const OpenShiftsPage = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await openShiftsAPI.listForOffice();
      setShifts(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load open shifts');
    } finally { setLoading(false); }
  };

  const rebroadcast = async (visitId) => {
    setBusyId(visitId);
    try {
      const res = await visitsAPI.broadcast(visitId, null);
      toast.success(`Re-broadcast — ${res.data.candidates_notified} caregivers notified`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Broadcast failed');
    } finally { setBusyId(null); }
  };

  const approve = async (visitId, caregiverUserId, caregiverName) => {
    if (!window.confirm(`Approve ${caregiverName} for this shift?`)) return;
    setBusyId(visitId);
    try {
      await visitsAPI.approveRequest(visitId, caregiverUserId);
      toast.success(`Assigned ${caregiverName} to the shift`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    } finally { setBusyId(null); }
  };

  const durationHours = (m) => (m ? (m / 60).toFixed(1) : '?');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="open-shifts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold flex items-center gap-2">
            <Broadcast weight="duotone" className="w-7 h-7 text-primary" />
            Open Shifts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">AI-dispatched shifts waiting for a caregiver. First to be approved by you gets it.</p>
        </div>
        <Button variant="outline" className="rounded-xl gap-2" onClick={load} data-testid="reload-open-shifts">
          <ArrowClockwise weight="bold" className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {shifts.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Broadcast weight="duotone" className="w-16 h-16 text-muted-foreground/40 mb-4" />
            <h3 className="font-heading text-lg font-medium">No open shifts</h3>
            <p className="text-muted-foreground text-sm mt-1">Unassigned visits automatically broadcast when scheduled.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {shifts.map(v => {
            const isExpanded = expandedId === v.id;
            const requests = v.broadcast_requests || [];
            const candidates = v.broadcast_candidates || [];
            const meta = statusMeta[v.broadcast_status] || statusMeta.open;
            return (
              <Card key={v.id} className="rounded-2xl border border-border" data-testid={`open-shift-card-${v.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <CardTitle className="font-heading text-lg">{v.client_name}</CardTitle>
                      <CardDescription className="mt-1 flex flex-wrap gap-3 text-xs">
                        <span className="flex items-center gap-1"><Clock weight="duotone" className="w-3.5 h-3.5" />{format(new Date(v.scheduled_date), 'EEE, MMM d')} · {v.scheduled_time} · {durationHours(v.duration_minutes)}h</span>
                        <span className="flex items-center gap-1"><MapPin weight="duotone" className="w-3.5 h-3.5" />{v.broadcast_radius_miles} mi radius</span>
                        <span className="flex items-center gap-1"><Users weight="duotone" className="w-3.5 h-3.5" />{candidates.length} notified</span>
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${meta.color} rounded-full text-xs`}>{meta.label}</Badge>
                      {requests.length > 0 && (
                        <Badge className="bg-amber-500 text-white rounded-full text-xs">{requests.length} request{requests.length > 1 ? 's' : ''}</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {requests.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2">Requests to Approve</p>
                      <div className="space-y-2">
                        {requests.map(req => {
                          const cand = candidates.find(c => c.caregiver_user_id === req.caregiver_user_id) || {};
                          return (
                            <div key={req.caregiver_user_id} className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-3" data-testid={`request-${v.id}-${req.caregiver_user_id}`}>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{req.caregiver_name}</p>
                                <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-2 mt-0.5">
                                  <span>{cand.distance_mi ?? '?'} mi</span>
                                  {cand.ai_acceptance_pct != null && (
                                    <span className="flex items-center gap-1"><Star weight="duotone" className="w-3 h-3 text-primary" />{cand.ai_score}/100</span>
                                  )}
                                  {cand.past_visits_with_client > 0 && <span>· {cand.past_visits_with_client} prior visits</span>}
                                </p>
                                {req.note && <p className="text-xs italic text-muted-foreground mt-1">&ldquo;{req.note}&rdquo;</p>}
                              </div>
                              <Button
                                className="rounded-xl gap-1"
                                onClick={() => approve(v.id, req.caregiver_user_id, req.caregiver_name)}
                                disabled={busyId === v.id}
                                data-testid={`approve-btn-${v.id}-${req.caregiver_user_id}`}
                              >
                                <CheckCircle weight="bold" className="w-4 h-4" />
                                Approve
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-xl text-xs"
                      onClick={() => setExpandedId(isExpanded ? null : v.id)}
                      data-testid={`toggle-candidates-${v.id}`}
                    >
                      {isExpanded ? 'Hide candidates' : `Show all ${candidates.length} candidate${candidates.length !== 1 ? 's' : ''} (AI ranked)`}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl gap-1"
                      onClick={() => rebroadcast(v.id)}
                      disabled={busyId === v.id}
                      data-testid={`rebroadcast-${v.id}`}
                    >
                      <Broadcast weight="bold" className="w-3.5 h-3.5" />
                      Re-broadcast
                    </Button>
                  </div>

                  {isExpanded && (
                    <div className="space-y-2 pt-2">
                      {candidates.length === 0 ? (
                        <p className="text-xs italic text-muted-foreground">No caregivers found within radius. Try Re-broadcast with a wider radius or check that caregivers have addresses on file.</p>
                      ) : candidates.map(c => (
                        <div key={c.caregiver_user_id} className="rounded-lg border border-border p-3 bg-muted/20" data-testid={`candidate-${v.id}-${c.caregiver_user_id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="font-medium text-sm">{c.caregiver_name}</p>
                              <p className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-0.5">
                                <span>{c.distance_mi} mi</span>
                                <span className="flex items-center gap-1"><Star weight="duotone" className="w-3 h-3 text-primary" />{c.ai_score}/100</span>
                                <span>{c.ai_acceptance_pct}% likely to accept</span>
                                {c.past_visits_with_client > 0 && <span>· {c.past_visits_with_client} prior visits</span>}
                              </p>
                            </div>
                          </div>
                          {c.ai_reason && <p className="text-[11px] text-muted-foreground mt-1 italic">Why: {c.ai_reason}</p>}
                          {c.ai_message && (
                            <div className="mt-2 pt-2 border-t border-border/40">
                              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1"><ChatDots weight="duotone" className="w-3 h-3" />AI-drafted offer sent</p>
                              <p className="text-xs">{c.ai_message}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OpenShiftsPage;
